import { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import {
  CircleMarker, LayersControl, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap, useMapEvents,
} from 'react-leaflet'
import { Box, Text } from '@ui'
import { getJSON } from '../api.js'
import { useStoredState } from '../storage.js'
import { formatTakenAt } from '../format.js'
import { groupPins } from '../photoPins.js'
import { PhotoUrlContext } from './Photos.jsx'

// OS styles served by our own proxy (/api/tiles), which holds the key and OS's 24-hour cache
// rule. Zoom limits match the app's for these styles: OS's _3857 tiles run from 7 to 20.
const OS_ATTRIBUTION = `Contains OS data © Crown copyright and database rights ${new Date().getFullYear()} · <a href="https://www.ordnancesurvey.co.uk/legal" target="_blank" rel="noreferrer">OS terms</a>`
// OpenStreetMap's tile policy: attribution with a link to the copyright page.
export const OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'
const STYLES = [
  { key: 'outdoor', name: 'OS Outdoor' },
  { key: 'road', name: 'OS Road' },
  { key: 'light', name: 'OS Light' },
]

// Mapbox Outdoors through Mapbox's Static Tiles API (512 px tiles, hence zoomOffset -1), offered
// when the server has a public token for this site (/api/map-config; the token is URL-restricted
// at Mapbox, so it only works from our own pages). Mapbox's attribution rules for its styles:
// its logo on the map, plus © Mapbox, © OpenStreetMap and "Improve this map".
const MAPBOX_NAME = 'Mapbox Outdoors'
const MAPBOX_ATTRIBUTION = '© <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noreferrer">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> <a href="https://apps.mapbox.com/feedback/" target="_blank" rel="noreferrer"><strong>Improve this map</strong></a>'

let mapConfig // fetched once per page load, shared by every map
function useMapConfig() {
  const [config, setConfig] = useState(null)
  useEffect(() => {
    mapConfig ??= getJSON('/api/map-config').catch(() => ({}))
    let live = true
    mapConfig.then((c) => live && setConfig(c))
    return () => { live = false }
  }, [])
  return config
}

// The map styles to choose from (the walk map's and the photos map's), OS Outdoor first unless
// this browser last chose another, and the matching logo on the map, bottom left. It waits for the
// map config (one small fetch per page load), since react-leaflet only reads `checked` when a
// layer mounts.
export function BaseLayers() {
  const config = useMapConfig()
  const [chosen, setChosen] = useStoredState('wl-map-style', STYLES[0].name)
  const map = useMapEvents({ baselayerchange: (e) => setChosen(e.name) })
  if (!config) return null
  const token = config.mapbox_token
  const available = [...STYLES.map((s) => s.name), ...(token ? [MAPBOX_NAME] : [])]
  const current = available.includes(chosen) ? chosen : STYLES[0].name
  return (
    <>
      <LayersControl position="topright">
        {STYLES.map((s) => (
          <LayersControl.BaseLayer key={s.key} name={s.name} checked={current === s.name}>
            <TileLayer url={`/api/tiles/${s.key}/{z}/{x}/{y}.png`} attribution={OS_ATTRIBUTION} minZoom={7} maxZoom={20} />
          </LayersControl.BaseLayer>
        ))}
        {token && (
          <LayersControl.BaseLayer key="mapbox" name={MAPBOX_NAME} checked={current === MAPBOX_NAME}>
            <TileLayer
              url={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${encodeURIComponent(token)}`}
              tileSize={512} zoomOffset={-1} attribution={MAPBOX_ATTRIBUTION} minZoom={7} maxZoom={20}
            />
          </LayersControl.BaseLayer>
        )}
      </LayersControl>
      {createPortal(
        current === MAPBOX_NAME
          ? <img className="map-logo" src="/mapbox-logo.svg" alt="Mapbox" />
          : <img className="map-logo" src="/os-logo-maps.png" alt="Ordnance Survey" />,
        map.getContainer(),
      )}
    </>
  )
}

function FitRoute({ positions }) {
  const map = useMap()
  useEffect(() => {
    if (positions.length > 1) map.fitBounds(positions, { padding: [24, 24] })
    else if (positions.length === 1) map.setView(positions[0], 16)
  }, [map, positions])
  return null
}

// A photo pin: the first photo's own thumbnail in a small white-ringed circle (a plain label for
// a video or photo the browser couldn't make a preview of), with a count when it stands for more.
function pinIcon(group, photoUrl) {
  const photo = group.pins[0].photo
  const inner = photo.has_preview === false
    ? `<span>${photo.media_type === 'video' ? '▶' : '▣'}</span>`
    : `<img src="${photoUrl(photo.id, 'thumb')}" alt="">`
  const count = group.pins.length > 1 ? `<span class="photo-pin-count">${group.pins.length}</span>` : ''
  return L.divIcon({
    className: `photo-pin${photo.media_type === 'video' ? ' photo-pin-video' : ''}`,
    html: inner + count,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  })
}

const kind = (photo) => (photo.media_type === 'video' ? 'video' : 'photo')

function pinLabel(group, walkStart) {
  const timed = group.pins.map((p) => p.photo.taken_at).filter(Boolean).sort()
  const byRoute = group.pins.some((p) => p.from === 'route') ? ' (placed on the route by time)' : ''
  if (group.pins.length === 1) {
    const { photo } = group.pins[0]
    const what = kind(photo) === 'video' ? 'Video' : 'Photo'
    return `${what}${timed.length ? `, taken ${formatTakenAt(timed[0], walkStart)}` : ''}${byRoute}`
  }
  const kinds = new Set(group.pins.map((p) => kind(p.photo)))
  const what = kinds.size > 1 ? 'photos and videos' : `${[...kinds][0]}s`
  const first = timed.length ? formatTakenAt(timed[0], walkStart) : null
  const last = timed.length ? formatTakenAt(timed[timed.length - 1], walkStart) : null
  const when = first ? (first === last ? `, taken ${first}` : `, taken ${first}–${last}`) : ''
  return `${group.pins.length} ${what}${when}${byRoute}`
}

// The photo pins, grouped where they'd overlap at the current zoom (regrouped on every zoom).
// Clicking a group zooms in until its photos come apart; if they'd still overlap at the closest
// zoom (taken at one spot), it opens the viewer at the group's first photo, and the viewer's
// arrows go through the rest. With onGroup (the photos map), a click hands the group over instead,
// and label(group) gives the pins' titles.
export function PhotoPins({ pins, onOpenPhoto, walkStart, onGroup, label }) {
  const map = useMap()
  const photoUrl = useContext(PhotoUrlContext)
  const [zoom, setZoom] = useState(() => map.getZoom())
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) })
  const groups = useMemo(
    () => groupPins(pins, (pin) => map.project([pin.lat, pin.lon], zoom)),
    [pins, map, zoom],
  )
  // Made once per grouping: hovering the chart re-renders the map, and a new icon would redraw it.
  const icons = useMemo(() => groups.map((g) => pinIcon(g, photoUrl)), [groups, photoUrl])

  const open = (group) => {
    if (onGroup) {
      onGroup(group)
      return
    }
    // Zoom in only as far as it takes for the group to come apart, centred on it.
    for (let z = zoom + 1; group.pins.length > 1 && z <= map.getMaxZoom(); z++) {
      if (groupPins(group.pins, (pin) => map.project([pin.lat, pin.lon], z)).length > 1) {
        map.setView(L.latLngBounds(group.pins.map((pin) => [pin.lat, pin.lon])).getCenter(), z)
        return
      }
    }
    onOpenPhoto?.(group.pins[0].index)
  }

  return groups.map((group, i) => (
    <Marker
      // Times in the key too: Leaflet doesn't update a marker's title, so a time set by hand needs a new one.
      key={group.pins.map((p) => `${p.photo.id}@${p.photo.taken_at}`).join('-')} position={[group.lat, group.lon]} icon={icons[i]}
      title={label ? label(group) : pinLabel(group, walkStart)} eventHandlers={{ click: () => open(group) }}
    />
  ))
}

// points: the walk page's track points; hover: index of the point the chart's crosshair is on;
// pins: from placePhotos (photoPins.js); onOpenPhoto(index) opens the viewer at that photo;
// walkStart: the walk's start, so a photo time on another day shows its date. tiles: 'os' (the
// signed-in pages, through the site's OS proxy) or 'osm' (shared pages: OpenStreetMap's own tiles,
// since the OS project isn't licensed for the public yet). trimmed: the route's ends are hidden, so
// no start and finish markers.
export default function WalkMap({ points, hover, pins = [], onOpenPhoto, walkStart, tiles = 'os', trimmed = false }) {
  const positions = useMemo(() => points.map((p) => [p.lat, p.lon]), [points])
  if (positions.length === 0) {
    return <Text as="p" muted>No usable GPS points to draw.</Text>
  }
  const start = positions[0]
  const finish = positions[positions.length - 1]
  const hovered = hover != null ? positions[hover] : null

  return (
    <Box position="relative">
      <MapContainer className="walk-map" center={start} zoom={14} minZoom={7} maxZoom={tiles === 'osm' ? 19 : 20} scrollWheelZoom={false}>
        {tiles === 'osm' ? (
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution={OSM_ATTRIBUTION} maxZoom={19} />
        ) : (
          <BaseLayers />
        )}
        {/* White casing under the route, as the app draws on OS layers, so it reads over busy maps. */}
        <Polyline positions={positions} pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.9 }} />
        <Polyline positions={positions} pathOptions={{ color: '#1565c0', weight: 4 }} />
        {/* A trimmed route's ends aren't the start and finish; marking them would point at what's hidden. */}
        {!trimmed && (
          <>
            <CircleMarker center={start} radius={7} pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#1b7f3b', fillOpacity: 1 }}>
              <Tooltip>Start</Tooltip>
            </CircleMarker>
            <CircleMarker center={finish} radius={7} pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#b3261e', fillOpacity: 1 }}>
              <Tooltip>Finish</Tooltip>
            </CircleMarker>
          </>
        )}
        <PhotoPins pins={pins} onOpenPhoto={onOpenPhoto} walkStart={walkStart} />
        {hovered && (
          <CircleMarker center={hovered} radius={6} pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#111111', fillOpacity: 1 }} />
        )}
        <FitRoute positions={positions} />
      </MapContainer>
    </Box>
  )
}
